import { useEffect, useMemo, useState } from 'react'
import { Input } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useTopics } from '@BUP/hooks/Resources/useTopics'
import { extractResourceLinks, RESOURCE_TYPES } from '@BUP/utils/topicResources'
import { trackEvent } from '@BUP/services/tracker'
import BupSpinner from '@BUP/components/Loading/Spinner'
import styles from './resource-search.module.scss'

// Accent/case-insensitive: "bullying" should still find "Bullying" or a topic with an accent in a
// nearby word, matching how a school staffer actually types a search term.
const normalize = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

// A topic's `category:X` tag (e.g. 'category:sana-convivencia') as a human-typeable phrase
// ('sana convivencia') - the `locale:`/`role:`/`level:` tags are deliberately never searched:
// they're internal routing slugs a person would never type (nobody searches "role:student"),
// whereas the category slugs read close enough to the actual Spanish category names to be worth
// matching even hyphenated.
const categoryLabelFromTags = (tags) => {
  const categoryTag = (tags || []).find(tag => tag.startsWith('category:'))
  return categoryTag ? categoryTag.replace('category:', '').replace(/-/g, ' ') : ''
}

/**
 * Flatten every topic in `topics` into its individual resource links (see extractResourceLinks) -
 * a "resource" here is one activity/article/video link, not a whole topic/category bundle. Each
 * carries every field a search can match against: its own title, plus the containing topic's
 * name/description/category - so typing the theme of a topic (its name, or "convivencia") surfaces
 * every resource inside it, not just ones whose own title happens to repeat that word.
 *
 * Normalized once per field here (this only reruns when `topics` itself changes - see the
 * `useMemo` at its call site), instead of re-normalizing all 4 fields on every keystroke: the raw
 * fields never change between keystrokes, only `normalizedQuery` does.
 */
const flattenResources = (topics) => {
  if (!topics) return []
  return topics.flatMap(topic => {
    const linksByType = extractResourceLinks(topic.content)
    const topicCategory = categoryLabelFromTags(topic.tags)
    return RESOURCE_TYPES.flatMap(resourceType =>
      linksByType[resourceType].map((link, index) => ({
        id: `${topic.id}-${resourceType}-${index}`,
        title: link.text,
        href: link.href,
        resourceType,
        topicId: topic.id,
        topicName: topic.name,
        topicDescription: topic.description,
        topicCategory,
        normalizedFields: [link.text, topic.name, topic.description, topicCategory].map(normalize)
      }))
    )
  })
}

// How long to wait after the last keystroke before actually filtering - typing several characters
// in quick succession only needs to run the filter once, on the final value.
const SEARCH_DEBOUNCE_MS = 200

/**
 * Delays reflecting `value` until `delayMs` have passed without it changing.
 */
const useDebouncedValue = (value, delayMs) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [value, delayMs])
  return debouncedValue
}

/**
 * Search box for individual resources (activity/article/video links) across every category of one
 * "section" (a role, e.g. student/teacher/counselor/parent - see resources-by-section.jsx), instead
 * of having to click into each category's Topic one at a time. `tags` is the section's scope
 * WITHOUT a `category:` tag (e.g. ['locale:es', 'role:student', 'level:beginner']) so every category
 * for that scope is fetched and searched at once.
 *
 * Matches (case/accent-insensitive substring) against SEARCHABLE_FIELDS: the resource's own title,
 * its containing topic's name and description, and that topic's category tag as a phrase.
 */
const ResourceSearch = ({ tags }) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.resources.search' })
  const [tSections] = useTranslation('translation', { keyPrefix: 'components.topic.content.sections' })
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)
  const [topics, isLoadingTopics] = useTopics(tags.join())

  const allResources = useMemo(() => flattenResources(topics), [topics])

  const normalizedQuery = normalize(debouncedQuery)
  const results = useMemo(() => {
    if (!normalizedQuery) return []
    return allResources.filter(resource =>
      resource.normalizedFields.some(field => field.includes(normalizedQuery)))
  }, [allResources, normalizedQuery])

  const handleClickResult = (resource) => {
    trackEvent('Open Searched Resource', {
      link: resource.title,
      url: resource.href,
      topicId: resource.topicId
    })
  }

  return (
    <div className={styles.container}>
      <Input
        allowClear
        placeholder={t('placeholder')}
        prefix={<SearchOutlined style={{ color: '#898a89' }} />}
        value={query}
        onChange={event => setQuery(event.target.value)}
        className={styles.search_input}
      />
      {normalizedQuery && isLoadingTopics && <BupSpinner size='small' />}
      {normalizedQuery && !isLoadingTopics && (
        results.length > 0
          ? (
            <>
              <p className={styles.results_count}>{t('resultsCount', { count: results.length })}</p>
              <ul className={styles.results_list}>
                {results.map(resource => (
                  <li key={resource.id} className={styles.result_item}>
                    <a
                      href={resource.href}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={() => handleClickResult(resource)}
                    >
                      {resource.title}
                    </a>
                    <span className={styles.result_meta}>
                      {tSections(resource.resourceType)} · {resource.topicName}
                    </span>
                  </li>
                ))}
              </ul>
            </>
            )
          : <p className={styles.no_results}>{t('noResults', { query })}</p>
      )}
    </div>
  )
}

export default ResourceSearch
